import { Box, Typography } from '@mui/material';
import { RefreshCw, RotateCcw } from 'lucide-react';
import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router';

import { createAdminApi, roleSatisfies, type AdminUserSummary, type SensitiveActionInput } from '@/admin/adminApi';
import { SensitiveActionDialog, type SensitiveActionConsequence } from '@/admin/SensitiveActionDialog';
import { SENSITIVE_ACTIONS } from '@/admin/sensitiveActions';
import { adminErrorCode, describeAdminError } from '@/admin/settings/adminErrorMessage';
import { formatInstant } from '@/admin/users/accountPresentation';
import { AccountIdentity, AccountsPager, LastActivity } from '@/admin/users/AccountsTable';
import { EMPTY_QUERY, serializeAccountQuery, type AccountListQuery } from '@/admin/users/accountFilters';
import { useAccountsList } from '@/admin/users/useAccountsList';
import { useAdminAccess } from '@/admin/users/useAdminAccess';
import { useRefreshOnFocus } from '@/admin/useRefreshOnFocus';
import { useHttpClient } from '@/app/providers';
import { NAV_ENTRIES, targetPath } from '@/ui/navigation';
import {
  AccessDenied,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  visuallyHidden,
  type DataTableColumn,
} from '@/ui/kit';

/**
 * Moderation (P11, BO-15) : les **comptes suspendus**, et rien d'autre.
 *
 * **Aucun mecanisme de signalement n'existe** dans l'application — ni cote
 * mobile, ni cote serveur — : la file de la maquette n'a aucune source, et
 * l'ecran ne la fabrique pas (ADR 0035, point 5). Il le dit en toutes lettres,
 * y compris quand la liste est vide : un ecran « Modération » vide sans
 * explication ferait croire que tout va bien, alors que rien n'est collecte.
 *
 * Le tableau est celui de la liste des comptes, filtre sur `state=suspended`
 * cote serveur. Le seul geste est **« Réactiver »**, via le dispositif commun
 * (`SensitiveActionDialog`) : motif type et confirmation par saisie de
 * l'adresse, sans mot de passe — la reactivation ne detruit rien et ne rend
 * aucune session. Le role `support` lit la liste **sans** bouton : le serveur
 * est l'autorite, l'ecran n'offre pas un geste qui mene a un 403.
 */

const SUSPENDED: AccountListQuery = { ...EMPTY_QUERY, view: 'suspended' };

const REACTIVATE = SENSITIVE_ACTIONS.reactivate;

const REACTIVATE_CONSEQUENCE: SensitiveActionConsequence = {
  summary: 'Le compte pourra de nouveau se connecter avec son mot de passe.',
  disappearsTitle: 'Ce qui change',
  disappears: ['La suspension prend fin : le compte n’apparaît plus dans cette liste'],
  keptTitle: 'Ce qui reste',
  kept: [
    'Les données du compte, ses profils bébé et ses partages : la suspension n’y a jamais touché',
    'Les sessions coupées à la suspension : la personne devra se reconnecter',
    'La trace de la suspension et de sa levée, au journal d’audit',
  ],
};

const reasonLabel = (code: string | null): string | null =>
  code === null ? null : (SENSITIVE_ACTIONS.suspend.reasons.find((reason) => reason.code === code)?.label ?? code);

/** Lien vers « Familles et partages » : il n'existe que si l'ecran est livre (BO-17). */
const familiesTarget = (): string | null => {
  const entry = NAV_ENTRIES.find((candidate) => candidate.id === 'families');

  return entry?.target === undefined ? null : targetPath(entry.target);
};

interface Pending {
  readonly account: AdminUserSummary;
  readonly busy: boolean;
  readonly error: { readonly message: string; readonly code: string | null } | null;
}

export const ModerationScreen = (): ReactElement => {
  const http = useHttpClient();
  const { access, reload: reloadAccess } = useAdminAccess();
  const [params, setParams] = useSearchParams();
  const pageParam = Number.parseInt(params.get('page') ?? '', 10);
  const page = Number.isInteger(pageParam) && pageParam >= 1 ? pageParam : 1;
  const query = useMemo(() => ({ ...SUSPENDED, page }), [page]);

  const [token, setToken] = useState(0);
  const [dialog, setDialog] = useState<Pending | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setToken((previous) => previous + 1);
  }, []);

  useRefreshOnFocus(refresh);

  const { data, failure, busy } = useAccountsList(query, { enabled: true, token });
  const role = access.kind === 'granted' ? access.role : null;
  const canReactivate = roleSatisfies(role, 'admin');
  const operatorEmail = access.kind === 'granted' ? access.identity.email : '';

  const items = data?.page.items ?? [];
  const total = data?.page.total ?? -1;
  const families = familiesTarget();

  const confirm = async (input: SensitiveActionInput): Promise<void> => {
    if (dialog === null) {
      return;
    }

    const { account } = dialog;
    setDialog({ account, busy: true, error: null });

    try {
      await createAdminApi(http).reactivateUser(account.id, input);
      setDialog(null);
      setDone(`Le compte ${account.email} est réactivé : il peut de nouveau se connecter.`);
      refresh();
    } catch (error: unknown) {
      setDialog({ account, busy: false, error: { message: describeAdminError(error), code: adminErrorCode(error) } });
    }
  };

  const columns: readonly DataTableColumn<AdminUserSummary>[] = [
    { key: 'account', header: 'Compte', primary: true, render: (account) => <AccountIdentity account={account} /> },
    { key: 'reason', header: 'Motif', render: (account) => reasonLabel(account.suspensionReasonCode) },
    { key: 'since', header: 'Suspendu depuis', render: (account) => (account.suspendedAt === null ? null : formatInstant(account.suspendedAt)) },
    { key: 'activity', header: 'Dernière activité', render: (account) => <LastActivity iso={account.lastActivityAt} now={data?.loadedAt ?? 0} /> },
    ...(canReactivate
      ? [
          {
            key: 'actions',
            header: 'Action',
            render: (account: AdminUserSummary) => (
              <Button
                size="S"
                icon={RotateCcw}
                onClick={() => {
                  setDone(null);
                  setDialog({ account, busy: false, error: null });
                }}
              >
                Réactiver
              </Button>
            ),
          } satisfies DataTableColumn<AdminUserSummary>,
        ]
      : []),
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <PageHeader
        eyebrow="Utilisateurs"
        title="Modération"
        subtitle="Les comptes suspendus. Aucun signalement ne remonte de l’application : les litiges arrivent par le support, et chaque décision est humaine, motivée et journalisée."
        actions={
          <Button icon={RefreshCw} onClick={refresh} disabled={busy}>
            {busy ? 'Actualisation…' : 'Actualiser'}
          </Button>
        }
      />

      {failure?.forbidden === true ? (
        <AccessDenied title="Votre compte n’a pas accès à l’administration">
          Vous êtes bien connecté, mais ce compte n’a aucun rôle d’administration. Un rôle s’attribue depuis le serveur,
          jamais depuis cet écran.
        </AccessDenied>
      ) : (
        <>
          {access.kind === 'error' ? <ErrorState message={access.message} onRetry={reloadAccess} /> : null}
          {failure === null ? null : <ErrorState message={failure.message} onRetry={refresh} />}

          <Box role="status" sx={done === null ? visuallyHidden : { px: 2, py: 1.25, borderRadius: '12px', bgcolor: 'tokens.successSoft', color: 'tokens.successText', fontSize: 14, fontWeight: 600 }}>
            {done}
          </Box>

          <Card
            title="Comptes suspendus"
            subtitle={total >= 0 ? `${total.toLocaleString('fr-FR')} compte${total > 1 ? 's' : ''}` : undefined}
          >
            {data === null && busy ? <LoadingState label="Chargement des comptes suspendus…" /> : null}

            {data !== null && items.length > 0 ? (
              <DataTable label="Comptes suspendus" columns={columns} rows={items} getRowId={(account) => account.id} />
            ) : null}

            {data !== null && items.length === 0 ? (
              <EmptyState
                title="Aucun compte suspendu"
                description={
                  <>
                    Aucun signalement n’existe dans l’application : les litiges arrivent par le support.
                    {families === null ? '' : ' Pour un partage, ouvrir Familles et partages.'}
                  </>
                }
                action={families === null ? undefined : <Button to={families}>Ouvrir Familles et partages</Button>}
              />
            ) : null}
          </Card>

          {data !== null && total >= 0 && items.length > 0 ? (
            <AccountsPager
              state={{ page: data.page.page, perPage: data.page.perPage, itemCount: items.length, total }}
              busy={busy}
              onPage={(next) => {
                setParams(serializeAccountQuery({ ...EMPTY_QUERY, page: next }));
              }}
            />
          ) : null}

          <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
            {canReactivate
              ? 'La suspension se décide depuis la fiche d’un compte ; ici, on la lève.'
              : 'Votre rôle permet de consulter cette liste, pas de lever une suspension.'}
          </Typography>
        </>
      )}

      {dialog === null ? null : (
        <SensitiveActionDialog
          open
          title="Réactiver ce compte"
          subtitle={dialog.account.email}
          consequence={REACTIVATE_CONSEQUENCE}
          reasonOptions={REACTIVATE.reasons}
          requiresPassword={REACTIVATE.requiresPassword}
          operatorEmail={operatorEmail}
          expectedConfirmation={dialog.account.email}
          confirmationLabel="l’adresse du compte"
          actionLabel="Réactiver le compte"
          pending={dialog.busy}
          error={dialog.error?.message ?? null}
          errorCode={dialog.error?.code ?? null}
          onCancel={() => {
            setDialog(null);
          }}
          onConfirm={(input) => {
            void confirm(input);
          }}
        />
      )}
    </Box>
  );
};
